import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Types of Operating System".
 *
 * Three sections that unlock in order:
 *
 *   Families   - meet all five, read what each is built for, then
 *                answer for it.
 *   Identify   - six machines, described only by how they behave. Work
 *                out which family is running, from the evidence.
 *   Patching   - who updates each of these, and what happens when the
 *                honest answer is nobody. This is the section the
 *                lesson is really for.
 *
 * Built from the same pieces as the components lab, on purpose: a
 * learner who has done that one already knows how this works, and the
 * styling is all there.
 */

const STORE_KEY = 'cybercub.lab.os-types';

/* ------------------------------------------------------------------ */
/* 1. the five families                                               */
/* ------------------------------------------------------------------ */

const FAMILIES = [
  {
    id: 'desktop',
    name: 'Desktop',
    icon: '🖥️',
    body: 'One person, many programs',
    examples: 'Windows · macOS · Linux',
    what: 'Built for somebody sitting in front of it, running whatever they like: a browser, a game, homework, all at once.',
    detail: 'It has to be general. It cannot know in advance what will be installed, so it says yes to almost everything and asks the person to decide.',
    security:
      'That openness is the whole problem. It is the family most attacked, because it is the one where a human can be talked into running something.',
    quiz: {
      q: 'Why is a desktop operating system harder to secure than a washing machine?',
      options: [
        'It has a faster processor',
        'It must run anything the user chooses to install',
        'It is connected to the internet and the washing machine is not',
        'It has more storage',
      ],
      answer: 1,
      explain: 'Generality costs security. A machine that can run anything can be made to run something bad.',
    },
  },
  {
    id: 'mobile',
    name: 'Mobile',
    icon: '📱',
    body: 'Touch, battery, sandboxes',
    examples: 'Android · iOS',
    what: 'Built for a device in a pocket: a touch screen, a battery that has to last the day, and apps from a shop rather than from anywhere.',
    detail: 'Each app runs in its own sandbox and must ask before it reaches your camera, your files or your location. Nothing is shared by default.',
    security:
      'Separation by default is a genuinely better design. The weak point moved: it is now the permission you granted without reading it.',
    quiz: {
      q: 'A game you installed wants to read your messages. On a mobile operating system it:',
      options: [
        'Can already read them',
        'Has to ask you, because apps cannot reach each other by default',
        'Can read them only over wifi',
        'Can never read them under any circumstances',
      ],
      answer: 1,
      explain: 'It has to ask. Which is why the dangerous moment is the prompt, not the install.',
    },
  },
  {
    id: 'server',
    name: 'Server',
    icon: '🗄️',
    body: 'Nobody sitting at it',
    examples: 'Linux · Windows Server',
    what: 'Built to serve many people at once and to keep running for years. Usually no screen, no keyboard, and nobody in the room.',
    detail: 'Everything is done remotely, over the network. It runs the smallest set of programs it can, because every extra one is another way in.',
    security:
      'No one is watching it. A desktop user notices their machine behaving oddly within minutes; a server can be compromised for months before anybody looks.',
    quiz: {
      q: 'What makes a break-in on a server especially dangerous?',
      options: [
        'Servers hold no useful data',
        'There is nobody sitting at it to notice anything is wrong',
        'Servers cannot be patched',
        'Servers have no passwords',
      ],
      answer: 1,
      explain: 'Nobody notices. That is why servers are watched by logs and alerts rather than by eyes.',
    },
  },
  {
    id: 'embedded',
    name: 'Embedded',
    icon: '🔌',
    body: 'One job, forever',
    examples: 'Routers · cameras · tills · cars',
    what: 'A tiny operating system doing one fixed job, in as little memory as the manufacturer could get away with.',
    detail: 'It is inside things nobody thinks of as computers. Your router is one. So is the card machine in a shop, and most of a modern car.',
    security:
      'Rarely updated, often still on the password it left the factory with, and connected to the internet anyway. Entire botnets have been built out of exactly this.',
    quiz: {
      q: 'Why are home routers and cameras such a popular target?',
      options: [
        'They store passwords in plain text',
        'They are almost never updated, and many keep their default password',
        'They have powerful processors',
        'They are easy to steal',
      ],
      answer: 1,
      explain: 'Nobody thinks of a camera as a computer, so nobody patches it. It sits online for years, unchanged.',
    },
  },
  {
    id: 'realtime',
    name: 'Real-time',
    icon: '⏱️',
    body: 'Late is the same as wrong',
    examples: 'Medical · industrial · aviation',
    what: 'Built to guarantee that it will answer within a fixed time, every time. Not fast on average - fast in the worst case.',
    detail: 'An infusion pump, a factory robot, an aircraft control surface. Being a second late is not a delay, it is a failure.',
    security:
      'It often cannot be patched when a flaw is found, because stopping it is itself dangerous. Hospitals live with this tension every day.',
    quiz: {
      q: 'A flaw is found in the software of a hospital infusion pump. Why is patching it so difficult?',
      options: [
        'The patch would be too large',
        'The machine cannot safely stop or be late while it is in use',
        'Hospitals are not connected to the internet',
        'Real-time systems cannot be changed at all',
      ],
      answer: 1,
      explain: 'Downtime is a risk to a patient. Safety and patching pull against each other, and neither side is obviously right.',
    },
  },
];

/* ------------------------------------------------------------------ */
/* 2. identify the machine                                            */
/* ------------------------------------------------------------------ */

const MACHINES = [
  {
    id: 'm1',
    icon: '🏪',
    text: 'A card machine in a shop. It does one thing, has been on the counter for four years, and nobody has ever logged into it.',
    answer: 'embedded',
    why: 'One fixed job, years without being touched. That is embedded, and the four years without an update is the worry.',
  },
  {
    id: 'm2',
    icon: '📈',
    text: 'It has been powered on for 412 days. There is no monitor plugged into it. Forty people use it at once without knowing it exists.',
    answer: 'server',
    why: 'Uptime measured in hundreds of days, no screen, many users at once. A server.',
  },
  {
    id: 'm3',
    icon: '🎮',
    text: 'Someone installed a game on it yesterday, a browser last week, and a printer driver they found through a search engine.',
    answer: 'desktop',
    why: 'A person choosing what to install, from anywhere. Only a desktop works that way - and that is exactly its weakness.',
  },
  {
    id: 'm4',
    icon: '🏭',
    text: 'It controls a robot arm on a production line. The manufacturer guarantees it will respond within two milliseconds, every time.',
    answer: 'realtime',
    why: 'A guaranteed worst-case response is the definition of real-time. Average speed is not the promise being made.',
  },
  {
    id: 'm5',
    icon: '📷',
    text: 'An app on it asked permission before using the camera, and a different app cannot see its files at all.',
    answer: 'mobile',
    why: 'Sandboxes and permission prompts. That separation by default is the mobile model.',
  },
  {
    id: 'm6',
    icon: '🚗',
    text: 'It is inside a car. It has no screen of its own, runs the same code it shipped with in 2019, and talks to the internet for traffic updates.',
    answer: 'embedded',
    why: 'Embedded, and a good example of why it matters: online, five years unpatched, and inside something moving.',
  },
];

/* ------------------------------------------------------------------ */
/* 3. who patches it                                                  */
/* ------------------------------------------------------------------ */

const PATCHING = [
  {
    id: 'p1',
    icon: '💻',
    text: 'Your laptop tells you an update is ready and asks to restart. You put it off for three weeks.',
    options: [
      'Harmless - updates are mostly new features',
      'The flaws it fixes are public by then, and being used',
      'The laptop will update itself anyway',
      'Updates make a computer slower, so waiting is sensible',
    ],
    answer: 1,
    why: 'Once a patch ships, the flaw it fixes is public knowledge. Waiting three weeks is three weeks of everyone knowing the way in.',
  },
  {
    id: 'p2',
    icon: '📡',
    text: 'Your home router has been plugged in for six years. Nobody has ever updated it, and the manufacturer stopped releasing updates in 2021.',
    options: [
      'Fine, routers do not run software',
      'It is unpatchable and permanently online - the worst combination there is',
      'It only matters if you use wifi',
      'The internet provider patches it remotely',
    ],
    answer: 1,
    why: 'No more updates, still online, still doing its job. This is the single most common weak point in a home.',
  },
  {
    id: 'p3',
    icon: '🏥',
    text: 'A hospital finds a serious flaw in the operating system of a machine that is currently keeping someone alive.',
    options: [
      'Patch it immediately, whatever happens',
      'Isolate it from the network and plan the patch around the patient',
      'Ignore it, medical devices are never attacked',
      'Turn it off until a fix arrives',
    ],
    answer: 1,
    why: 'Neither patching blindly nor ignoring it is safe. You reduce what can reach it, and you fix it when fixing it is safe.',
  },
  {
    id: 'p4',
    icon: '📱',
    text: 'A phone stops receiving security updates from its manufacturer after four years, but still works perfectly.',
    options: [
      'Nothing changes - it still works',
      'It keeps working, and stops being defended. Those are different things',
      'Apps will refuse to open',
      'The phone becomes slower on purpose',
    ],
    answer: 1,
    why: 'Working and safe are not the same. A phone with no updates is a phone whose known flaws stay open forever.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (state) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch { /* private window */ }
};

export default function OsTypesLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('families');
  const [solved, setSolved] = useState(() => new Set(saved.solved ?? []));
  const [read, setRead] = useState(() => new Set(saved.read ?? []));
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState(null);
  const [machinePicks, setMachinePicks] = useState(saved.machinePicks ?? {});
  const [patchPicks, setPatchPicks] = useState(saved.patchPicks ?? {});

  useEffect(() => {
    save({ solved: [...solved], read: [...read], machinePicks, patchPicks });
  }, [solved, read, machinePicks, patchPicks]);

  useEffect(() => { document.title = 'Types of Operating System · CyberCub Lab'; }, []);

  const familiesDone = solved.size === FAMILIES.length;
  const machinesDone = MACHINES.every((m) => machinePicks[m.id] === m.answer);
  const patchingDone = PATCHING.every((p) => patchPicks[p.id] === p.answer);

  useFinished('/lab/os-types', patchingDone);

  const TABS = [
    { id: 'families', n: 1, label: 'The five families', locked: false },
    { id: 'identify', n: 2, label: 'Which one is running?', locked: !familiesDone },
    { id: 'patching', n: 3, label: 'Who patches it?', locked: !machinesDone },
  ];

  const openFamily = (f) => {
    setOpen(f);
    setPicked(solved.has(f.id) ? f.quiz.answer : null);
  };

  const answer = (index) => {
    setPicked(index);
    if (index === open.quiz.answer) setSolved((prev) => new Set(prev).add(open.id));
  };

  const back = (() => {
    if (tab === 'families') return open ? { label: '← Back to the families', act: () => setOpen(null) } : null;
    if (tab === 'identify') return { label: '← Back to the families', act: () => setTab('families') };
    return { label: '← Back to the machines', act: () => setTab('identify') };
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Types of Operating System</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf2-operating-systems" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. the families ---------------- */}
      {tab === 'families' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>The five families</h2>
              <p className="lab-lead">
                Every one of these does the same job — share the machine out, keep programs apart,
                talk to the hardware. What differs is what each is allowed to be bad at.
              </p>
            </div>
            {back && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
            )}
          </div>

          <Tutor
            avatar="cub-sky"
            say={
              familiesDone
                ? 'All five answered. Notice what they have in common: every one of them is a set of trade-offs, and the security of each follows from what it was built to be good at.'
                : 'Tap a family to read what it is built for, then answer its question. You need all five right to move on. There is no trick to it — the answer is always in what the machine has to do for a living.'
            }
          />

          <div className="lab-progress">
            <div className="row spread">
              <strong>Progress</strong>
              <span>{solved.size} / {FAMILIES.length}</span>
            </div>
            <div className="lab-bar">
              <span style={{ width: `${(solved.size / FAMILIES.length) * 100}%` }} />
            </div>
          </div>

          <div className="lab-grid">
            {FAMILIES.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`lab-part ${solved.has(f.id) ? 'is-seen' : ''} ${open?.id === f.id ? 'is-open' : ''}`}
                onClick={() => openFamily(f)}
              >
                <span className="lab-part-icon" aria-hidden="true">{f.icon}</span>
                <strong>{f.name}</strong>
                <span className="lab-part-body">{f.body}</span>
                {solved.has(f.id) && <span className="lab-tick" aria-label="answered correctly">✓</span>}
              </button>
            ))}
          </div>

          {familiesDone && (
            <div className="lab-done">
              <strong>All five.</strong> Now work out which one is running on a machine you cannot see.
              <button type="button" className="btn btn-sm" onClick={() => setTab('identify')}>
                Which one is running? →
              </button>
            </div>
          )}

          {open && (
            <div className="lab-detail" role="dialog" aria-label={open.name}>
              <div className="row spread">
                <div className="row gap-sm">
                  <span className="lab-part-icon" aria-hidden="true">{open.icon}</span>
                  <div>
                    <h2>{open.name}</h2>
                    <span className="lab-part-body">{open.examples}</span>
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(null)}>
                  Close
                </button>
              </div>

              <p>{open.what}</p>
              <p className="muted">{open.detail}</p>
              <div className="lab-note">
                <strong>Why it matters for security</strong>
                <p>{open.security}</p>
              </div>

              {/* The question stays hidden until the theory is marked read,
                  so the answer cannot be picked off the screen first. */}
              {!read.has(open.id) && !solved.has(open.id) ? (
                <div className="lab-readgate">
                  <p className="small muted">Read the description above, then carry on to the question.</p>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setRead((prev) => new Set(prev).add(open.id))}
                  >
                    ✓ Complete — show the question
                  </button>
                </div>
              ) : (
                <div className="lab-mcq">
                  <h3>{solved.has(open.id) ? '✓ ' : ''}{open.quiz.q}</h3>
                  <div className="lab-options">
                    {open.quiz.options.map((opt, i) => {
                      const isPicked = picked === i;
                      const isRight = i === open.quiz.answer;
                      const state = picked === null ? '' : isRight ? 'is-right' : isPicked ? 'is-wrong' : '';
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={`lab-option ${state} ${isPicked ? 'is-picked' : ''}`}
                          onClick={() => answer(i)}
                          disabled={solved.has(open.id)}
                        >
                          <span className="lab-option-mark">{'ABCD'[i]}</span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>

                  {picked !== null && (
                    <p className={`lab-feedback ${picked === open.quiz.answer ? 'is-right' : 'is-wrong'}`}>
                      {picked === open.quiz.answer
                        ? `✓ ${open.quiz.explain}`
                        : '✗ Not quite — read the description again and try another answer.'}
                    </p>
                  )}

                  {solved.has(open.id) && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        const next = FAMILIES.find((f) => !solved.has(f.id));
                        if (next) openFamily(next);
                        else setOpen(null);
                      }}
                    >
                      {FAMILIES.some((f) => !solved.has(f.id)) ? 'Next family →' : 'Done'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. identify the machine ---------------- */}
      {tab === 'identify' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Which one is running?</h2>
              <p className="lab-lead">
                Six machines, described only by how they behave. You cannot see any of them — work
                out the family from the evidence, which is all anyone gets from the outside.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {MACHINES.filter((m) => machinePicks[m.id] === m.answer).length} / {MACHINES.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-mint"
            say={
              machinesDone
                ? 'Six from six. What you just did has a name — fingerprinting. Working out what something runs without being able to look at it is the first thing an attacker does, and the first thing a defender has to assume they have done.'
                : 'Read each description and ask what the machine has to do for a living. How long has it been on? Who uses it? Does anybody ever touch it? The behaviour gives the family away.'
            }
          />

          {MACHINES.map((m, i) => {
            const pick = machinePicks[m.id];
            const right = pick === m.answer;
            return (
              <div key={m.id} className={`lab-case ${right ? 'is-right' : pick ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{m.icon}</span>
                <div className="grow">
                  <p><strong>Machine {i + 1}.</strong> {m.text}</p>
                  <div className="lab-choices">
                    {FAMILIES.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className={`lab-choice ${pick === f.id ? 'is-picked' : ''}`}
                        onClick={() => setMachinePicks((p) => ({ ...p, [m.id]: f.id }))}
                        disabled={right}
                      >
                        {f.icon} {f.name}
                      </button>
                    ))}
                  </div>
                  {pick && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${m.why}` : '✗ Not that one — read what the machine actually does again.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {machinesDone && (
            <div className="lab-done">
              <strong>All six identified.</strong> Now the question that decides whether any of them
              is safe.
              <button type="button" className="btn btn-sm" onClick={() => setTab('patching')}>
                Who patches it? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. who patches it ---------------- */}
      {tab === 'patching' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Who patches it?</h2>
              <p className="lab-lead">
                Every operating system has flaws found in it. What separates a safe machine from an
                unsafe one is not the flaws — it is whether anybody ever fixes them.
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
                {back.label}
              </button>
              <span className="pill">
                {PATCHING.filter((p) => patchPicks[p.id] === p.answer).length} / {PATCHING.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              patchingDone
                ? 'That is the whole lesson in one idea. Working and safe are different things, and the gap between them is measured in updates nobody installed.'
                : 'Four situations. For each one, work out what the update situation really means — not whether the machine still works, but whether anybody is still defending it.'
            }
          />

          {PATCHING.map((p, i) => {
            const pick = patchPicks[p.id];
            const right = pick === p.answer;
            return (
              <div key={p.id} className={`lab-case ${right ? 'is-right' : pick !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{p.icon}</span>
                <div className="grow">
                  <p><strong>Situation {i + 1}.</strong> {p.text}</p>
                  <div className="lab-choices">
                    {p.options.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick === oi ? 'is-picked' : ''}`}
                        onClick={() => setPatchPicks((prev) => ({ ...prev, [p.id]: oi }))}
                        disabled={right}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right ? `✓ ${p.why}` : '✗ Not that one — think about what stops being true once updates stop.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {patchingDone && (
            <div className="lab-done">
              <div>
                <strong>All four.</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  Five families, five sets of trade-offs, and one thing they all share: an operating
                  system nobody updates is a machine nobody is defending, however well it still works.
                </p>
              </div>
              <Link to="/learn/lesson/cf2-operating-systems" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
